/**
 * useSseStream — hook for consuming a Server-Sent Events stream with
 * an Authorization header.
 *
 * Native EventSource doesn't support custom headers, so we build a
 * minimal SSE consumer on top of fetch + ReadableStream. This is
 * ~50 lines and avoids pulling in a third-party SSE client.
 *
 * The hook opens the stream when `url` and `accessToken` are both set,
 * tears it down on unmount or when those inputs change. Events are
 * delivered to the `onEvent` callback as they arrive. Stream errors
 * surface via the `onError` callback (network drops, abort, etc.).
 */

import { useEffect, useRef } from 'react';

export interface SseEvent {
  type: string;
  data: string;
}

interface UseSseStreamOptions {
  url: string | null;
  accessToken: string | null;
  onEvent: (event: SseEvent) => void;
  onError?: (error: Error) => void;
}

export function useSseStream({ url, accessToken, onEvent, onError }: UseSseStreamOptions): void {
  // Stash the latest callbacks in refs so the effect doesn't tear down
  // the stream every time the parent re-renders with a new closure.
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
  }, [onEvent, onError]);

  useEffect(() => {
    if (!url || !accessToken) return;

    const abortController = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'text/event-stream',
          },
          signal: abortController.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error(`Stream open failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE event boundaries are blank lines (\n\n).
          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const parsed = parseEvent(rawEvent);
            if (parsed) onEventRef.current(parsed);
            boundary = buffer.indexOf('\n\n');
          }
        }
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [url, accessToken]);
}

/**
 * Parse a single SSE event block. Returns null for heartbeats / comments.
 *
 * Format per the SSE spec:
 *   event: <type>
 *   data: <payload>
 * (multiple data: lines concatenated with \n)
 *
 * Lines starting with ":" are comments (we use them as heartbeats server-side).
 *
 * Exported for unit testing.
 */
export function parseEvent(raw: string): SseEvent | null {
  const lines = raw.split('\n');
  let type = 'message';
  const dataLines: string[] = [];
  let hasContent = false;

  for (const line of lines) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    // SSE spec: strip exactly one leading space (not all leading whitespace).
    let value = line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') {
      type = value;
      hasContent = true;
    } else if (field === 'data') {
      dataLines.push(value);
      hasContent = true;
    }
    // ignore id:, retry:, and unknown fields for now
  }

  if (!hasContent) return null;
  return { type, data: dataLines.join('\n') };
}
